import { classifyStage1DocumentsBatch } from './stage1-batch-classify-run';
import { isFolderImportedDocument, stage1TextDocumentRoute } from './stage1-import-completion';
import { markStage1PreMadeScopeDocument, selectStage1PreMadeScopeDocument } from './stage1-premade-scope';
import { createSelectedFileScope, fileMatchesSelectedScope } from './selected-file-scope';
import {
    assertEvidencePreparationCurrent, checkpointPreparedEvidence,
    type ProjectEvidencePreparationOptions, type ProjectEvidencePreparationResult,
} from './project-evidence-preparation-types';

type Row = Record<string, any>;

/**
 * A Stage 1 drop classifies its documents with the AI and marks a client's own
 * Scope of Works (Stage1DataEntry, handleDocsFiles). Documents that arrived by
 * Import Projects or the Quote Run skipped that import, so they kept only the
 * rule-based class and an RFQ that arrives as a photo or an email could not be
 * picked out (Peter, 2026-10-23: the two routes must behave the same). This
 * classifies such documents once, the same way, before Summary reads them.
 * A document set that went through a Stage 1 import is left alone.
 *
 * A plain text file Stage 1 puts in Inspector Notes, or keeps as estimator
 * notes, never reaches that batch; a notes file is kept as a 'notes_source'
 * document, as Stage 1 keeps it. Summary adds its text to the notes.
 */
export async function prepareProjectDocumentClassification(
    options: ProjectEvidencePreparationOptions,
    overrides: { classify?: typeof classifyStage1DocumentsBatch } = {},
): Promise<ProjectEvidencePreparationResult> {
    assertEvidencePreparationCurrent(options);
    const project = options.project;
    const documents: Row[] = Array.isArray(project.documents) ? project.documents : [];
    const scope = createSelectedFileScope(options.selectedFileIds);
    const folderDocuments = documents.filter(document => fileMatchesSelectedScope(document, scope)
        && isFolderImportedDocument(document));
    const textRoute = new Map(folderDocuments.map(document => [document, stage1TextDocumentRoute(document)]));
    const notesFiles = folderDocuments.filter(document => textRoute.get(document) === 'inspector_notes'
        && document.type !== 'notes_source');
    // The folder documents of this run not yet classified, as one batch, as a
    // Stage 1 drop classifies the batch it imports.
    const selected = folderDocuments.filter(document => !document.aiClassification
        && textRoute.get(document) !== 'inspector_notes' && textRoute.get(document) !== 'estimator_notes');
    const copies = selected.map(document => ({ ...document }));
    let classified = false;
    if (options.apiKey && copies.length > 0) {
        options.onProgress?.(0, 'Classifying documents...');
        await (overrides.classify || classifyStage1DocumentsBatch)(copies, {
            apiKey: options.apiKey, model: options.model, shouldStop: () => !!options.signal?.aborted,
        });
        assertEvidencePreparationCurrent(options);
        // A failed call leaves no AI class on any document; the next preparation tries again.
        classified = copies.some(document => document.aiClassification);
    }
    if (!classified && notesFiles.length === 0) return { project, changed: false };
    const replaced = new Map<Row, Row>();
    if (classified) {
        const scopeDocument = selectStage1PreMadeScopeDocument(copies);
        if (scopeDocument) markStage1PreMadeScopeDocument(scopeDocument);
        selected.forEach((document, index) => replaced.set(document, copies[index]!));
    }
    for (const document of notesFiles) replaced.set(document, { ...document, type: 'notes_source' });
    const next = { ...project, documents: documents.map(document => replaced.get(document) || document) };
    return { project: await checkpointPreparedEvidence(options, next), changed: true };
}
